/**
 * Transaction data export — the platform's transport (`/api/txn-export`).
 *
 * The Export window, the template manager and the editor are shared with the
 * downloaded runtime (`@twasta/page-runtime` txn/export); this is the
 * platform's implementation of their `ExportTransport`. Run-as headers (a
 * project user in the Run view) are attached by the shared axios client.
 */
import api from './client'
import type {
  ExportInfo, ExportJob, ExportPreview, ExportTemplate, ExportTransport, ManageTemplates,
} from '@twasta/page-runtime/txn'

const enc = encodeURIComponent

export function platformExportTransport(project: string): ExportTransport {
  const base = `/txn-export/${enc(project)}`
  return {
    async info(obj) {
      return (await api.get(`${base}/info`, { params: { obj } })).data as ExportInfo
    },
    async preview(body) {
      return (await api.post(`${base}/preview`, body)).data as ExportPreview
    },
    async start(body) {
      return (await api.post(`${base}/start`, body)).data as ExportJob
    },
    async job(jobId) {
      return (await api.get(`${base}/jobs/${enc(jobId)}`)).data as ExportJob
    },
    async cancel(jobId) {
      await api.delete(`${base}/jobs/${enc(jobId)}`)
    },
    async download(jobId) {
      const res = await api.get(`${base}/jobs/${enc(jobId)}/file`, { responseType: 'blob' })
      return res.data as Blob
    },
    async manageTemplates(obj) {
      return (await api.get(`${base}/templates`, { params: { obj } })).data as ManageTemplates
    },
    async saveTemplate(obj, template, expectVersion) {
      const res = await api.put(`${base}/templates`, {
        obj, template, ...(expectVersion != null ? { expect_version: expectVersion } : {}),
      })
      return (res.data?.template ?? res.data) as ExportTemplate
    },
    async deleteTemplate(obj, templateId) {
      await api.delete(`${base}/templates`, { params: { obj, id: templateId } })
    },
  }
}
